/* OnSumn — ShareCardPopover
 *
 * The full SHARE panel. Morphs out of the card's share icon (FLIP), renders
 * a live, branded PNG of the event (flyer + title + venue + date + category,
 * generated by lib/share-card.js), and offers every realistic path to get
 * that show onto a social platform:
 *
 *   • Share        — native Web Share sheet WITH the image file attached, so
 *                    on mobile the user can drop it straight into Instagram /
 *                    TikTok / Stories / Snapchat / WhatsApp (the apps that
 *                    refuse prefilled links). Falls back to link+text share,
 *                    then clipboard.
 *   • Save image   — downloads the PNG to post manually.
 *   • Copy link    — onsumn.com/e/{slug} to clipboard.
 *   • X · Reddit · Telegram · Facebook — prefilled deep links (OG image
 *                    unfurls carry the visual on these).
 *   • Instagram · TikTok — Stories path: native file-share on mobile, or
 *                    save-the-card + an instruction on desktop.
 *
 * PATTERN — ports MerchPopover.jsx: portal-to-body, FLIP morph, scroll lock,
 * focus management, Esc, prefers-reduced-motion, theme-aware glass tokens.
 *
 * Window-attached at file bottom per OnSumn convention. ShareIconButton
 * (Interest.jsx) mounts this when window.ShareCardPopover is a function;
 * otherwise it degrades to the legacy window.shareEvent native sheet.
 */

// Official brand marks (monochrome, currentColor → theme-aware) for the
// share targets. Paths are the canonical Simple Icons glyphs for each app.
const SHARE_BRAND_PATHS = {
  instagram: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163C8.741 0 8.333.015 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.015 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.015 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
  x: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  tiktok: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
};

function BrandIcon({ name }) {
  const d = SHARE_BRAND_PATHS[name];
  if (!d) return null;
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"
      aria-hidden="true" focusable="false" style={{ display: 'block' }}>
      <path d={d} />
    </svg>
  );
}

function ShareCardPopover({ event, darkMode, triggerRect, onClose }) {
  const panelRef = React.useRef(null);
  const closeBtnRef = React.useRef(null);
  const previouslyFocusedRef = React.useRef(null);
  const [closing, setClosing] = React.useState(false);
  const [card, setCard] = React.useState({ status: 'loading', dataUrl: '', file: null });

  const reducedMotion = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const toast = (msg, tone) => {
    if (typeof window !== 'undefined' && typeof window.onsumnToast === 'function') {
      window.onsumnToast(msg, tone ? { tone } : undefined);
    }
  };

  // ── share strings (reuse lib/share.js builders for voice parity) ──
  // `ev` is a null-safe view — these run on every render BEFORE the
  // `if (!event...) return null` guard below (can't early-return between
  // hooks), so dereference defensively even though both call sites already
  // gate on event.slug.
  const ev = event || {};
  const slug = ev.slug;
  const shareUrl = (typeof window.buildShareUrl === 'function' && slug)
    ? window.buildShareUrl(slug)
    : (slug ? 'https://onsumn.com/e/' + slug : 'https://onsumn.com');
  const shareText = (typeof window.buildShareText === 'function')
    ? window.buildShareText({ title: ev.title, venue: ev.venue, date: ev.date })
    : `Pulling up to ${ev.title} — Slide don't hide`;
  const shareTitle = (typeof window.buildShareTitle === 'function')
    ? window.buildShareTitle({ title: ev.title, venue: ev.venue, date: ev.date })
    : (ev.title || 'OnSumn');

  // ── generate the card image on mount ──
  React.useEffect(() => {
    let alive = true;
    if (typeof window === 'undefined' || !window.OnSumnShareCard
        || typeof window.OnSumnShareCard.render !== 'function') {
      setCard({ status: 'error', dataUrl: '', file: null });
      return;
    }
    window.OnSumnShareCard.render(event, { darkMode })
      .then((res) => { if (alive) setCard({ status: res && res.dataUrl ? 'ready' : 'error', dataUrl: (res && res.dataUrl) || '', file: (res && res.file) || null }); })
      .catch(() => { if (alive) setCard({ status: 'error', dataUrl: '', file: null }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── FLIP morph (open) ──
  React.useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (reducedMotion || !triggerRect) {
      panel.style.transition = 'opacity 200ms ease';
      panel.style.opacity = '0';
      panel.getBoundingClientRect();
      requestAnimationFrame(() => { panel.style.opacity = '1'; });
      return;
    }
    const toRect = panel.getBoundingClientRect();
    const dx = triggerRect.left - toRect.left;
    const dy = triggerRect.top - toRect.top;
    const sx = Math.max(0.05, triggerRect.width / toRect.width);
    const sy = Math.max(0.05, triggerRect.height / toRect.height);
    panel.style.transformOrigin = 'top left';
    panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    panel.style.opacity = '0.85';
    panel.getBoundingClientRect();
    requestAnimationFrame(() => {
      panel.style.transition =
        'transform 420ms cubic-bezier(0.16, 1, 0.3, 1), opacity 240ms ease 60ms';
      panel.style.transform = 'translate(0, 0) scale(1, 1)';
      panel.style.opacity = '1';
    });
  }, [triggerRect, reducedMotion]);

  // ── Esc + scroll lock + focus ──
  React.useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = setTimeout(() => {
      if (closeBtnRef.current) closeBtnRef.current.focus();
    }, 200);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(focusTimer);
      if (previouslyFocusedRef.current && typeof previouslyFocusedRef.current.focus === 'function') {
        try { previouslyFocusedRef.current.focus(); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── FLIP morph (close) ──
  const handleClose = React.useCallback(() => {
    if (closing) return;
    const panel = panelRef.current;
    if (!panel || reducedMotion || !triggerRect) { onClose(); return; }
    setClosing(true);
    const fromRect = panel.getBoundingClientRect();
    const dx = triggerRect.left - fromRect.left;
    const dy = triggerRect.top - fromRect.top;
    const sx = Math.max(0.05, triggerRect.width / fromRect.width);
    const sy = Math.max(0.05, triggerRect.height / fromRect.height);
    panel.style.transition =
      'transform 320ms cubic-bezier(0.4, 0, 1, 1), opacity 200ms ease 80ms';
    panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    panel.style.opacity = '0';
    setTimeout(() => onClose(), 320);
  }, [closing, onClose, reducedMotion, triggerRect]);

  // ── actions ──
  const generating = card.status === 'loading';

  // Can this browser attach the card IMAGE to the native share sheet?
  // True on Safari iOS + Android Chrome. FALSE on Brave/Chrome on iOS
  // (WKWebView-based) — those can only share a URL, which is useless for
  // posting an image to Instagram/TikTok, so we save the card for them
  // instead of silently degrading to a bare link.
  const canFileShare = !!(card.file
    && typeof navigator !== 'undefined'
    && navigator.canShare
    && navigator.canShare({ files: [card.file] }));

  // Save (download) the generated PNG. On iOS this lands in Files/Photos;
  // the user can long-press the visible preview too. Returns true on success.
  const onSave = React.useCallback((msg) => {
    if (!card.dataUrl) { toast("card's still rendering — one sec", 'error'); return false; }
    try {
      const a = document.createElement('a');
      a.href = card.dataUrl;
      a.download = 'onsumn-' + ((event.slug || 'event').replace(/[^a-z0-9-]+/gi, '-').slice(0, 60)) + '.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast(msg || 'card saved — drop it on your story');
      return true;
    } catch { toast("couldn't save the card", 'error'); return false; }
  }, [card.dataUrl, event.slug]);

  const onNativeShare = React.useCallback(async () => {
    // Share the LINK only — Messages/WhatsApp render ONE tappable preview card
    // from the /e/{slug} OG meta. Attaching the generated PNG too made iOS show
    // TWO cards (the image attachment AND the unfurled link). The branded card
    // stays for Save + IG/TikTok stories (onStory) — those can't post a tappable
    // link, so they still need the image.
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    try {
      if (nav && typeof nav.share === 'function') {
        await nav.share({ title: shareTitle, text: shareText, url: shareUrl });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      toast('link copied — slide it to the group chat');
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      try { await navigator.clipboard.writeText(shareUrl); toast('link copied — slide it to the group chat'); }
      catch { toast("couldn't open the share sheet", 'error'); }
    }
  }, [shareText, shareTitle, shareUrl]);

  const onCopy = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast('link copied — slide it to the group chat');
    } catch { toast("couldn't copy link", 'error'); }
  }, [shareUrl]);

  const openPlatform = React.useCallback((href) => {
    window.open(href, '_blank', 'noopener,noreferrer');
  }, []);

  // Instagram / TikTok: deliver the card IMAGE to the story. Those apps don't
  // accept an auto-added clickable link from the web (the only tappable link in
  // a Story is the "Link" sticker, which the user adds in-app). So we pre-copy
  // the event link to the clipboard first — IG/TikTok suggest the clipboard URL
  // when adding a Link sticker, making it ~one tap to make the story tappable.
  const onStory = React.useCallback(async (appName) => {
    if (generating) { toast("hang tight — the card's still rendering"); return; }
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    // Fire-and-forget (NOT awaited) so it doesn't consume the user-activation
    // that nav.share needs on the very next line.
    try { if (navigator.clipboard) navigator.clipboard.writeText(shareUrl); } catch (_e) {}
    if (card.file && nav && nav.canShare && nav.canShare({ files: [card.file] })) {
      try {
        await nav.share({ files: [card.file], text: shareText });
        toast(`link copied — in ${appName}, add a Link sticker to make it tappable`);
        return;
      } catch (err) { if (err && err.name === 'AbortError') return; }
    }
    onSave(`saved + link copied — in ${appName}, add a Link sticker to your story`);
  }, [generating, card.file, shareText, shareUrl, onSave]);

  if (!event || !event.slug) return null;

  // ── theme tokens (mirror MerchPopover) ──
  const surface = darkMode ? '#121414' : '#F8F8F8';
  const onSurface = darkMode ? '#E2E2E2' : '#1A1A1A';
  const muted = darkMode ? 'rgba(226,226,226,0.58)' : 'rgba(26,26,26,0.62)';
  const amberReadable = darkMode ? '#D57800' : 'oklch(0.46 0.13 60)';
  const ruleColor = darkMode ? 'rgba(213,120,0,0.55)' : 'rgba(139,74,0,0.6)';
  const hairline = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)';
  const previewBg = darkMode ? 'oklch(0.20 0.01 70)' : 'oklch(0.95 0.005 70)';

  const panelGlass = typeof window !== 'undefined' && typeof window.glassStyle === 'function'
    ? window.glassStyle(darkMode, 'panel') : { background: surface };
  const btnGlass = typeof window !== 'undefined' && typeof window.glassStyle === 'function'
    ? window.glassStyle(darkMode, 'btn') : {};

  const stop = (e) => e.stopPropagation();

  // ── reusable button styles ──
  const ghostBtn = {
    ...btnGlass,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    minHeight: 48, padding: '12px 14px',
    border: darkMode ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(0,0,0,0.1)',
    borderRadius: 12,
    color: onSurface,
    fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 800,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    cursor: 'pointer',
    transition: reducedMotion ? 'none' : 'transform 140ms cubic-bezier(0.16,1,0.3,1)',
  };
  const platformBtn = {
    ...ghostBtn,
    fontSize: 12, minHeight: 46, padding: '10px 8px',
    flexDirection: 'column', gap: 4, letterSpacing: '0.06em',
  };
  const press = (e) => { if (!reducedMotion) e.currentTarget.style.transform = 'translateY(-2px)'; };
  const unpress = (e) => { if (!reducedMotion) e.currentTarget.style.transform = 'translateY(0)'; };

  const links = (typeof window.OnSumnShareCard !== 'undefined'
    && typeof window.OnSumnShareCard.platformLinks === 'function')
    ? window.OnSumnShareCard.platformLinks({ url: shareUrl, text: shareText, title: shareTitle })
    : {};

  // Share targets shown to the user: Instagram · X · TikTok (in that order).
  // Reddit / Telegram / Facebook are intentionally hidden — the deep-link
  // builder still produces them in lib/share-card.js if we want them back.

  const overlay = (
    <div
      role="presentation"
      onClick={(e) => { stop(e); handleClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: darkMode ? 'rgba(8,8,10,0.55)' : 'rgba(40,30,20,0.45)',
        backdropFilter: 'blur(10px) saturate(120%)',
        WebkitBackdropFilter: 'blur(10px) saturate(120%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(12px, 4vw, 32px)',
        animation: reducedMotion ? 'none' : 'share-card-backdrop-in 220ms ease both',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-card-headline"
        onClick={stop}
        style={{
          ...panelGlass,
          ...(!darkMode && { background: 'rgba(255,255,255,0.96)' }),
          border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)',
          borderRadius: 24,
          width: 'min(440px, 100%)',
          maxHeight: '92vh', overflowY: 'auto',
          padding: 'clamp(18px, 4vw, 24px)',
          position: 'relative',
          willChange: 'transform, opacity',
          boxShadow: (panelGlass.boxShadow || '') +
            ', 0 30px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)',
        }}
      >
        {/* Top bar — stamp + close */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: amberReadable, padding: '5px 10px',
            border: `1.5px solid ${amberReadable}`, borderRadius: 2,
            transform: 'rotate(-1.2deg)', transformOrigin: 'left center', whiteSpace: 'nowrap',
          }}>SHARE THE SLIDE</span>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={handleClose}
            aria-label="Close share panel"
            style={{
              ...btnGlass, width: 44, height: 44, borderRadius: '50%',
              border: darkMode ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(0,0,0,0.1)',
              color: onSurface, fontFamily: 'var(--font-display)',
              fontSize: 20, fontWeight: 700, lineHeight: 1, padding: 0, cursor: 'pointer',
              flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: reducedMotion ? 'none' : 'transform 200ms cubic-bezier(0.16,1,0.3,1)',
            }}
            onMouseEnter={(e) => { if (!reducedMotion) e.currentTarget.style.transform = 'rotate(90deg)'; }}
            onMouseLeave={(e) => { if (!reducedMotion) e.currentTarget.style.transform = 'rotate(0deg)'; }}
          >×</button>
        </div>

        <h2 id="share-card-headline" style={{
          fontFamily: 'var(--font-display)', fontWeight: 900,
          fontSize: 'clamp(24px, 6vw, 32px)', lineHeight: 0.98,
          letterSpacing: '-0.02em', color: onSurface, margin: '0 0 14px',
          textTransform: 'uppercase', textWrap: 'balance',
        }}>
          Put it everywhere<span style={{ color: 'var(--international-orange)' }}>.</span>
        </h2>

        {/* ── live card preview (4:5) ── */}
        <div style={{
          position: 'relative', width: '100%', aspectRatio: '4 / 5',
          background: previewBg, border: `1px solid ${hairline}`,
          borderRadius: 14, overflow: 'hidden', marginBottom: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {card.status === 'ready' && card.dataUrl ? (
            <img
              src={card.dataUrl}
              alt={`Shareable card for ${event.title}`}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : card.status === 'error' ? (
            <div style={{
              padding: 24, textAlign: 'center', fontFamily: 'var(--font-body)',
              fontSize: 13, color: muted, lineHeight: 1.5,
            }}>
              Couldn't render the image — links below still work.
            </div>
          ) : (
            <div className="share-card-skel" aria-live="polite" aria-busy="true" style={{
              width: '100%', height: '100%',
              background: darkMode
                ? 'linear-gradient(100deg, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.10) 50%, rgba(255,255,255,0.04) 70%)'
                : 'linear-gradient(100deg, rgba(0,0,0,0.04) 30%, rgba(0,0,0,0.08) 50%, rgba(0,0,0,0.04) 70%)',
              backgroundSize: '200% 100%',
              animation: reducedMotion ? 'none' : 'share-card-shimmer 1.2s linear infinite',
            }} />
          )}
        </div>

        {/* ── primary actions ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
          <button
            type="button" onClick={onNativeShare} onMouseEnter={press} onMouseLeave={unpress}
            disabled={generating}
            aria-label={generating ? 'Preparing the card' : (canFileShare ? 'Share the card image' : 'Save the card so you can post it')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              minHeight: 50, padding: '12px 14px', borderRadius: 12, border: 0,
              background: 'var(--international-orange)', color: 'var(--on-international-orange)',
              fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 800,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              opacity: generating ? 0.6 : 1,
              cursor: generating ? 'progress' : 'pointer',
              boxShadow: `4px 4px 0 0 ${darkMode ? '#0a0a0a' : 'oklch(0.35 0.12 60)'}`,
              transition: reducedMotion ? 'none' : 'transform 140ms ease, box-shadow 140ms ease',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 16 }}>{generating ? '◍' : '⬆'}</span> {generating ? 'Preparing…' : 'Share'}
          </button>
          <button type="button" onClick={() => onSave()} onMouseEnter={press} onMouseLeave={unpress}
            aria-label="Save the card image" style={ghostBtn}>
            <span aria-hidden="true" style={{ fontSize: 15 }}>⬇</span> Save
          </button>
          <button type="button" onClick={onCopy} onMouseEnter={press} onMouseLeave={unpress}
            aria-label="Copy the event link" style={ghostBtn}>
            <span aria-hidden="true" style={{ fontSize: 15 }}>⧉</span> Copy
          </button>
        </div>

        {/* Save-flow hint — only on browsers that can't attach the image to the
            share sheet (Brave/Chrome on iOS). Tells the user Share + the
            IG/TikTok buttons save the card so they can post it manually,
            instead of looking broken. */}
        {card.status === 'ready' && !canFileShare && (
          <p style={{
            margin: '0 0 14px', display: 'flex', gap: 8,
            fontFamily: 'var(--font-body)', fontSize: 12, lineHeight: 1.5,
            color: muted, letterSpacing: '0.01em',
          }}>
            <span aria-hidden="true" style={{ color: amberReadable, fontWeight: 700, flex: '0 0 auto' }}>↳</span>
            <span>
              This browser can't post images straight to apps. <strong style={{ color: onSurface, fontWeight: 700 }}>Share</strong> and the <strong style={{ color: onSurface, fontWeight: 700 }}>Instagram / TikTok</strong> buttons save the card to your device — or press &amp; hold the card above — then open the app and add it to your story.
            </span>
          </p>
        )}

        {/* ── platform deep links ── */}
        <p style={{
          margin: '6px 0 10px', display: 'flex', alignItems: 'center', gap: 12,
          fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 800,
          letterSpacing: '0.2em', textTransform: 'uppercase', color: amberReadable,
        }}>
          Post it
          <span aria-hidden="true" style={{ flex: '1 1 auto', height: 1, background: ruleColor }} />
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
          <button type="button" onClick={() => onStory('Instagram')}
            onMouseEnter={press} onMouseLeave={unpress}
            aria-label="Share to Instagram" style={platformBtn}>
            <BrandIcon name="instagram" /> Instagram
          </button>
          <button type="button" onClick={() => openPlatform(links.x)}
            onMouseEnter={press} onMouseLeave={unpress}
            aria-label="Share on X, opens in a new tab" style={platformBtn}>
            <BrandIcon name="x" /> X
          </button>
          <button type="button" onClick={() => onStory('TikTok')}
            onMouseEnter={press} onMouseLeave={unpress}
            aria-label="Share to TikTok" style={platformBtn}>
            <BrandIcon name="tiktok" /> TikTok
          </button>
        </div>

        {/* ── voice footer ── */}
        <p style={{
          margin: 0, display: 'flex', gap: 8,
          fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
          color: muted, lineHeight: 1.55, letterSpacing: '0.01em',
        }}>
          <span aria-hidden="true" style={{ color: amberReadable, fontWeight: 700, flex: '0 0 auto' }}>↳</span>
          Tap Share to drop the card straight into your story, or Save it and post it your way. Pull up loud.
        </p>
      </div>
    </div>
  );

  if (typeof window !== 'undefined'
      && window.ReactDOM
      && typeof window.ReactDOM.createPortal === 'function'
      && typeof document !== 'undefined') {
    return window.ReactDOM.createPortal(overlay, document.body);
  }
  return overlay;
}

// Keyframes (backdrop fade + skeleton shimmer) — injected once.
if (typeof document !== 'undefined' && !document.getElementById('share-card-popover-keyframes')) {
  const style = document.createElement('style');
  style.id = 'share-card-popover-keyframes';
  style.textContent =
    '@keyframes share-card-backdrop-in { from { opacity: 0; } to { opacity: 1; } }' +
    '@keyframes share-card-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }' +
    '@media (prefers-reduced-motion: reduce){ .share-card-skel{ animation: none !important; } }';
  document.head.appendChild(style);
}

window.ShareCardPopover = ShareCardPopover;
